import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("loading");
  const [details, setDetails] = useState(null);
  const { refresh } = useAuth();

  useEffect(() => {
    if (!orderId) { setStatus("error"); return; }
    api.get(`/paypal/order/${orderId}`)
      .then((r) => {
        setDetails(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          refresh();
        } else if (r.data.status === "approved" || r.data.status === "created") {
          setStatus("processing");
        } else {
          setStatus("error");
        }
      })
      .catch(() => setStatus("error"));
  }, [orderId, refresh]);

  return (
    <div className="min-h-[70vh] max-w-2xl mx-auto px-4 md:px-8 py-20 text-center" data-testid="payment-success-page">
      <div className="bg-[#151A14] border-2 border-[#2C332A] p-10">
        {(status === "loading" || status === "processing") && (
          <div data-testid="payment-polling">
            <Loader2 className="w-12 h-12 text-[#DD5422] animate-spin mx-auto mb-6" />
            <h1 className="font-anton text-4xl uppercase tracking-tight">Confirmin' your order...</h1>
            <p className="text-[#8D9688] mt-3">Hold tight, partner. We're checkin' with PayPal.</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 className="w-16 h-16 text-[#4CAF50] mx-auto mb-6" />
            <h1 className="font-anton text-5xl uppercase tracking-tight">Yeehaw!</h1>
            <p className="text-[#E5E5E5] mt-3 text-lg">
              Payment confirmed. {details?.metadata?.type === "subscription"
                ? "You're officially VIP."
                : `Your '${details?.metadata?.product_name || "order"}' is gettin' packed.`}
            </p>
            <p className="text-[#8D9688] mt-2 text-sm uppercase tracking-widest">
              ${(details?.amount || 0).toFixed(2)} {details?.currency?.toUpperCase()}
              {details?.metadata?.variant_label && ` · ${details.metadata.variant_label}`}
              {details?.metadata?.quantity && ` · Qty ${details.metadata.quantity}`}
            </p>
            {details?.metadata?.type === "merch" && (
              <div className="mt-6 text-xs text-[#8D9688] uppercase tracking-widest border-2 border-[#2C332A] p-4">
                Order forwarded to Printful for fulfillment.<br />
                Email confirmation comin' your way.
              </div>
            )}
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/shop"><Button data-testid="continue-shopping-btn" className="bg-[#DD5422] text-[#0A0E0B] hover:bg-[#FF6B35] rounded-none font-anton uppercase tracking-widest border-2 border-[#DD5422] h-12 px-8">Keep Shoppin'</Button></Link>
              <Link to="/orders"><Button variant="outline" className="bg-transparent border-2 border-[#2C332A] hover:border-[#DD5422] rounded-none font-anton uppercase tracking-widest h-12 px-8">My Orders</Button></Link>
            </div>
          </div>
        )}
        {status === "error" && (
          <div data-testid="payment-failed">
            <XCircle className="w-16 h-16 text-[#D32F2F] mx-auto mb-6" />
            <h1 className="font-anton text-4xl uppercase tracking-tight">Hold Up</h1>
            <p className="text-[#8D9688] mt-3">We couldn't confirm your payment. If it cleared on your end, check My Orders or hit us up.</p>
            <Link to="/shop"><Button className="mt-6 bg-[#DD5422] text-[#0A0E0B] hover:bg-[#FF6B35] rounded-none font-anton uppercase tracking-widest border-2 border-[#DD5422] h-12 px-8">Back to Shop</Button></Link>
          </div>
        )}
      </div>
    </div>
  );
}
